import React from "react";
import { motion } from "framer-motion";

// Hand-drawn, self-animating line illustrations. Each stroke "draws" itself
// on mount (pages remount on every flip, so it replays each turn).

const INK = "#2B4A3B";
const BRASS = "#D1A562";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  show: (i = 0) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.18 },
      opacity: { duration: 0.3, delay: 0.15 + i * 0.18 },
    },
  }),
};

const common = {
  fill: "none",
  stroke: INK,
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  variants: draw,
};

function Frame({ children }) {
  return (
    <motion.svg
      viewBox="0 0 220 150"
      initial="hidden"
      animate="show"
      className="w-full h-full"
      aria-hidden
    >
      {children}
    </motion.svg>
  );
}

const SHAPES = {
  candle: (
    <Frame>
      <motion.path {...common} custom={0} d="M110 60 v55" />
      <motion.path {...common} custom={1} d="M92 115 h36 l-4 14 h-28 z" />
      <motion.path {...common} custom={2} stroke={BRASS} d="M110 60 c-9 -10 -9 -20 0 -30 c9 10 9 20 0 30 z" />
      <motion.path {...common} custom={3} d="M70 100 q40 20 80 0" />
    </Frame>
  ),
  twoPaths: (
    <Frame>
      <motion.path {...common} custom={0} d="M20 75 h30" />
      <motion.path {...common} custom={1} stroke={BRASS} d="M50 75 C 90 45, 130 40, 200 25" />
      <motion.path {...common} custom={2} d="M50 75 C 90 100, 110 120, 130 100 C 150 82, 170 130, 200 120" />
      <motion.circle {...common} custom={3} cx="200" cy="25" r="6" fill={BRASS} stroke={BRASS} />
      <motion.circle {...common} custom={3} cx="200" cy="120" r="5" />
    </Frame>
  ),
  anyBusiness: (
    <Frame>
      <motion.path {...common} custom={0} d="M40 120 v-45 l30 -22 l30 22 v45 z" />
      <motion.path {...common} custom={1} d="M55 120 v-25 h30 v25" />
      <motion.path {...common} custom={2} d="M120 120 v-55 h55 v55 z" />
      <motion.path {...common} custom={3} d="M132 90 h12 M152 90 h12 M132 105 h12 M152 105 h12" />
      <motion.path {...common} custom={4} stroke={BRASS} d="M147 45 c-8 -9 -22 0 0 15 c22 -15 8 -24 0 -15 z" />
    </Frame>
  ),
  bucket: (
    <Frame>
      <motion.path {...common} custom={0} d="M70 55 h80 l-10 55 h-60 z" />
      <motion.path {...common} custom={1} d="M70 55 q40 -18 80 0" />
      <motion.path {...common} custom={2} stroke={BRASS} d="M100 118 q-3 8 0 12 q3 -4 0 -12" fill={BRASS} />
      <motion.path {...common} custom={3} stroke={BRASS} d="M120 122 q-3 8 0 12 q3 -4 0 -12" fill={BRASS} />
      <motion.path {...common} custom={4} d="M60 145 h100" />
    </Frame>
  ),
  doctor: (
    <Frame>
      <motion.path {...common} custom={0} d="M70 40 v30 a25 25 0 0 0 50 0 v-30" />
      <motion.path {...common} custom={1} d="M95 115 a20 20 0 1 0 0.1 0" />
      <motion.path {...common} custom={2} d="M95 115 v-20" />
      <motion.path {...common} custom={3} stroke={BRASS} d="M150 95 l12 14 l24 -34" />
      <motion.circle {...common} custom={4} cx="95" cy="135" r="9" />
    </Frame>
  ),
  growth: (
    <Frame>
      <motion.path {...common} custom={0} d="M35 125 v-95 M35 125 h150" />
      <motion.path {...common} custom={1} stroke={BRASS} d="M45 110 C 90 108, 120 70, 175 35" />
      <motion.path {...common} custom={2} stroke={BRASS} d="M160 35 h15 v15" fill="none" />
      <motion.circle {...common} custom={3} cx="70" cy="98" r="3.5" />
      <motion.circle {...common} custom={3} cx="110" cy="80" r="3.5" />
      <motion.circle {...common} custom={3} cx="145" cy="55" r="3.5" />
    </Frame>
  ),
  partner: (
    <Frame>
      <motion.circle {...common} custom={0} cx="88" cy="75" r="35" />
      <motion.circle {...common} custom={1} stroke={BRASS} cx="132" cy="75" r="35" />
      <motion.path {...common} custom={2} d="M105 55 q5 20 -0 40 M115 55 q-5 20 0 40" />
    </Frame>
  ),
  road: (
    <Frame>
      <motion.path {...common} custom={0} d="M55 140 C 80 90, 90 70, 110 30" />
      <motion.path {...common} custom={1} d="M165 140 C 140 90, 130 70, 110 30" />
      <motion.path {...common} custom={2} stroke={BRASS} strokeDasharray="1 12" d="M110 138 V 40" />
      <motion.circle {...common} custom={3} stroke={BRASS} cx="110" cy="28" r="9" fill={BRASS} />
    </Frame>
  ),
  map: (
    <Frame>
      <motion.path {...common} custom={0} d="M55 45 l35 -12 l40 14 l35 -12 v70 l-35 12 l-40 -14 l-35 12 z" />
      <motion.path {...common} custom={1} d="M90 33 v70 M130 47 v70" />
      <motion.path {...common} custom={2} stroke={BRASS} d="M110 60 a12 12 0 0 0 -12 12 c0 9 12 22 12 22 c0 0 12 -13 12 -22 a12 12 0 0 0 -12 -12 z" fill="none" />
      <motion.circle {...common} custom={3} stroke={BRASS} cx="110" cy="72" r="4" fill={BRASS} />
    </Frame>
  ),
  globe: (
    <Frame>
      <motion.circle {...common} custom={0} cx="110" cy="75" r="45" />
      <motion.path {...common} custom={1} d="M65 75 h90 M110 30 v90" />
      <motion.path {...common} custom={2} d="M110 30 c-28 15 -28 75 0 90 M110 30 c28 15 28 75 0 90" />
      <motion.circle {...common} custom={3} stroke={BRASS} cx="88" cy="58" r="4" fill={BRASS} />
      <motion.circle {...common} custom={3} stroke={BRASS} cx="132" cy="90" r="4" fill={BRASS} />
    </Frame>
  ),
  network: (
    <Frame>
      <motion.path {...common} custom={0} d="M110 75 L60 45 M110 75 L160 45 M110 75 L60 110 M110 75 L160 110" />
      <motion.circle {...common} custom={1} stroke={BRASS} cx="110" cy="75" r="12" fill="none" />
      <motion.circle {...common} custom={2} cx="60" cy="45" r="7" />
      <motion.circle {...common} custom={2} cx="160" cy="45" r="7" />
      <motion.circle {...common} custom={3} cx="60" cy="110" r="7" />
      <motion.circle {...common} custom={3} cx="160" cy="110" r="7" />
    </Frame>
  ),
  coins: (
    <Frame>
      <motion.ellipse {...common} custom={0} cx="90" cy="112" rx="30" ry="10" />
      <motion.path {...common} custom={1} d="M60 112 v-14 a30 10 0 0 0 60 0 v14" />
      <motion.ellipse {...common} custom={2} stroke={BRASS} cx="90" cy="90" rx="30" ry="10" />
      <motion.path {...common} custom={2} stroke={BRASS} d="M60 90 v-14 a30 10 0 0 0 60 0 v14" />
      <motion.ellipse {...common} custom={3} stroke={BRASS} cx="140" cy="70" rx="22" ry="8" />
      <motion.path {...common} custom={3} stroke={BRASS} d="M118 70 v-30 a22 8 0 0 0 44 0 v30" />
    </Frame>
  ),
  plate: (
    <Frame>
      <motion.ellipse {...common} custom={0} cx="110" cy="118" rx="55" ry="12" />
      <motion.path {...common} custom={1} d="M65 112 a45 40 0 0 1 90 0" />
      <motion.path {...common} custom={2} d="M110 72 v-8" />
      <motion.path {...common} custom={3} stroke={BRASS} d="M95 55 q15 -18 30 0 M110 55 c-8 -8 -20 0 0 12 c20 -12 8 -20 0 -12 z" fill="none" />
    </Frame>
  ),
};

export default function Sketch({ name }) {
  return SHAPES[name] || null;
}
